"use client"

import { yupResolver } from "@hookform/resolvers/yup"
import { useRouter } from "next/navigation"
import { signIn } from "next-auth/react"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { RiEyeFill, RiEyeOffFill, RiLoginBoxLine, RiQuestionLine } from "react-icons/ri"
import { toast } from "react-toastify"

import { Button } from "@/components/Button"
import { ErrorText } from "@/components/ErrorText"
import { useLocaleConfig } from "@/providers"
import { useLocaleStrings } from "@/providers/localeStrings/client"
import theme from "@/theme"
import { getAuthsignalClient } from "@/utils/authsignal"
import { getSignInSchema } from "@/utils/validation"

import { Card, Footer, H2, Input, Loader } from "./styled"

/** This component renders the sign in form card. */
export const FormCard = () => {
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)

  const locale = useLocaleConfig().locale ?? "de-DE"

  const {
    AUTH_SIGNIN_BUTTON_FORGOT_ACCESS,
    AUTH_SIGNIN_BUTTON_SUBMIT,
    AUTH_SIGNIN_TITLE,
    ERROR_CREDENTIALS,
    ERROR_MSG_EMAIL_INVALID,
    ERROR_MSG_REQUIRED,
    ERROR_MFA_TOKEN_INVALID,
    ERROR_GENERAL_LOGIN,
  } = useLocaleStrings()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(getSignInSchema({ email: ERROR_MSG_EMAIL_INVALID, required: ERROR_MSG_REQUIRED })),
  })

  /**
   * @param {{ email: string, password: string }}
   */
  const onSubmit = async ({ email, password }) => {
    setIsLoading(true)
    try {
      const isEnrolledRequest = await fetch("/api/auth/enroll-check", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email.toLowerCase() }),
      })

      const { isEnrolled, mfaDisabled } = await isEnrolledRequest.json()

      let signinResult = null
      if (isEnrolled && !mfaDisabled) {
        const authsignal = getAuthsignalClient()

        const signInToken = await authsignal.passkey.signIn()

        if (signInToken?.error) {
          toast.error(ERROR_MFA_TOKEN_INVALID, { theme: "dark" })
          setIsLoading(false)
          return
        }

        signinResult = await signIn("credentials", {
          signInToken: signInToken?.data?.token,
          email,
          password,
          deviceId: authsignal.anonymousId,
          redirect: false,
        })
      } else {
        signinResult = await signIn("credentials", {
          redirect: false,
          email,
          password,
        })
      }

      if (signinResult.error) {
        setIsLoading(false)
        toast.error(ERROR_CREDENTIALS, { theme: "dark" })
      } else {
        setIsLoading(false)
        router.replace(`/${locale}/auth/redirect-signin`)
      }
    } catch (_error) {
      setIsLoading(false)
      toast.error(ERROR_GENERAL_LOGIN, { theme: "dark" })
    }
  }

  const [showPassword, setShowPassword] = useState(false)

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)}>
        <H2>{AUTH_SIGNIN_TITLE}</H2>

        <Input
          label="Email"
          error={errors.email && <ErrorText message={errors.email.message} />}
          data-testid="login-email-input"
          {...register("email")}
        />

        <Input
          label="Password"
          type={showPassword ? "text" : "password"}
          error={errors.password && <ErrorText message={errors.password.message} />}
          data-testid="login-password-input"
          {...register("password")}
          auxiliarText={
            <Button handleClick={togglePasswordVisibility}>
              {showPassword ? (
                <RiEyeOffFill size={theme.iconSizes.small} />
              ) : (
                <RiEyeFill size={theme.iconSizes.small} />
              )}
            </Button>
          }
        />

        <Footer>
          <Button rounded secondary submit disabled={isLoading} data-testid="login-submit-button">
            {isLoading ? <Loader /> : <RiLoginBoxLine size={theme.iconSizes.normal} />}

            {AUTH_SIGNIN_BUTTON_SUBMIT}
          </Button>

          <Button href={`/${locale}/auth/forgot-password`} rounded seo disabled={isLoading}>
            <RiQuestionLine size={theme.iconSizes.normal} />
            {AUTH_SIGNIN_BUTTON_FORGOT_ACCESS}
          </Button>
        </Footer>
      </form>
    </Card>
  )
}
